import { BusinessObjectBase, decodeDecimals } from './base';
import { processDesignSchema } from '@schemas/business-objects/process-design';
import type { ProcessDesignActivityCount } from './process-design-activity-count';
import type { ProcessDesignHistory } from './process-design-history';
import type { ProcessInstance } from './process-instance';
import type { ProcessInstanceSummary } from './process-instance-summary';
import type { Tenant } from './tenant';
import type { Id, Json, Uuid } from '../base-types';
import { processAppEventSchema } from '@schemas/interface-definitions/process-app-event';
import { processDataTypeSchema } from '@schemas/business-objects/process-data-type';
import { processFunctionSchema } from '@schemas/business-objects/process-function';
import { processInfoSchema } from '@schemas/interface-definitions/process-info';
import { processServiceSchema } from '@schemas/business-objects/process-service';
import { processUserInterfaceSchema } from '@schemas/business-objects/process-user-interface';
import { roleInfoSchema } from '@schemas/interface-definitions/role-info';
import type {
  ProcessAppEvent,
  ProcessDataType,
  ProcessFunction,
  ProcessInfo,
  ProcessService,
  ProcessUserInterface,
  RoleInfo,
} from '../interface-definitions/index';
import type { ActivityMapping } from './activity-mapping';
import type { LatestProcessDesign } from './latest-process-design';
import type { ProcessMessage } from './process-message';
import type { ProcessRole } from './process-role';
import type { Token } from './token';

export interface ProcessDesignData {
  readonly id: Id;
  readonly designUuid: Uuid;
  readonly name?: string;
  readonly displayName?: string;
  readonly description?: string;
  readonly version: number;
  readonly suspended?: boolean;
  readonly sha1Sum?: string;
  readonly designJson?: Json;
  readonly analysis?: Json;
  readonly processInstances?: ProcessInstance[];
  readonly processDesignHistories?: ProcessDesignHistory[];
  readonly processDesignActivityCounts?: ProcessDesignActivityCount[];
  readonly processInstanceSummaries?: ProcessInstanceSummary[];
  readonly tenantId: Id;
  readonly tenant?: Tenant;
  readonly createdAt?: Date;
  readonly updatedAt?: Date;
}

export interface ProcessDesign extends Readonly<ProcessDesignData> {}
type ProcessDesignCreateWithChildrenData = Partial<
  Omit<ProcessDesignData, 'processInstances' | 'processDesignHistories'>
> & {
  processInstances?: Partial<ProcessInstance>[];
  processDesignHistories?: Partial<ProcessDesignHistory>[];
};

export class ProcessDesign extends BusinessObjectBase {
  static readonly entityName = 'ProcessDesign' as const;
  static readonly plural = 'process-designs';
  static schema = processDesignSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ProcessDesign[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ProcessDesign(data));
  }

  static async findOne(filter?: any): Promise<ProcessDesign | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ProcessDesign(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ProcessDesign> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ProcessDesign(data);
  }

  static async create(
    data: Omit<ProcessDesignData, 'id'>,
  ): Promise<ProcessDesign> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ProcessDesign(result);
  }

  static async createMany(
    data: Omit<ProcessDesignData, 'id'>[],
  ): Promise<ProcessDesign[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessDesign(item));
  }

  static async createWithChildren(
    data: ProcessDesignCreateWithChildrenData,
  ): Promise<ProcessDesign> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ProcessDesign(result);
  }

  static async createManyWithChildren(
    data: ProcessDesignCreateWithChildrenData[],
  ): Promise<ProcessDesign[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessDesign(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ProcessDesignData>,
  ): Promise<ProcessDesign> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ProcessDesign(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async getMigrationCandidateCount(): Promise<number> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessDesign.plural}/${(this as any).id}/get-migration-candidate-count`;
    return BusinessObjectBase.get<number>(url);
  }
  async getMigrationOverview(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessDesign.plural}/${(this as any).id}/get-migration-overview`;
    return BusinessObjectBase.post<any>(url, {});
  }
  async migrateReadyInstances(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessDesign.plural}/${(this as any).id}/migrate-ready-instances`;
    return BusinessObjectBase.post<any>(url, options);
  }
  async activate(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessDesign.plural}/${(this as any).id}/activate`;
    return BusinessObjectBase.post<any>(url, {});
  }
  static async getAppEvents(): Promise<ProcessAppEvent[]> {
    const url = `${this.baseUrl}/${this.plural}/get-app-events`;
    return this.get<ProcessAppEvent[]>(url).then((values) =>
      values?.map((value) => decodeDecimals(processAppEventSchema, value)),
    );
  }
  static async getDataTypes(): Promise<ProcessDataType[]> {
    const url = `${this.baseUrl}/${this.plural}/get-data-types`;
    return this.get<ProcessDataType[]>(url).then((values) =>
      values?.map((value) => decodeDecimals(processDataTypeSchema, value)),
    );
  }
  static async getFunctions(): Promise<ProcessFunction[]> {
    const url = `${this.baseUrl}/${this.plural}/get-functions`;
    return this.get<ProcessFunction[]>(url).then((values) =>
      values?.map((value) => decodeDecimals(processFunctionSchema, value)),
    );
  }
  static async getProcesses(): Promise<ProcessInfo[]> {
    const url = `${this.baseUrl}/${this.plural}/get-processes`;
    return this.get<ProcessInfo[]>(url).then((values) =>
      values?.map((value) => decodeDecimals(processInfoSchema, value)),
    );
  }
  static async getRoles(): Promise<RoleInfo[]> {
    const url = `${this.baseUrl}/${this.plural}/get-roles`;
    return this.get<RoleInfo[]>(url).then((values) =>
      values?.map((value) => decodeDecimals(roleInfoSchema, value)),
    );
  }
  static async getServices(): Promise<ProcessService[]> {
    const url = `${this.baseUrl}/${this.plural}/get-services`;
    return this.get<ProcessService[]>(url).then((values) =>
      values?.map((value) => decodeDecimals(processServiceSchema, value)),
    );
  }
  static async getUserInterfaces(): Promise<ProcessUserInterface[]> {
    const url = `${this.baseUrl}/${this.plural}/get-user-interfaces`;
    return this.get<ProcessUserInterface[]>(url).then((values) =>
      values?.map((value) => decodeDecimals(processUserInterfaceSchema, value)),
    );
  }
  static async loadFromFiles(): Promise<
    Array<{ name: string; version: number; status: string }>
  > {
    const url = `${this.baseUrl}/${this.plural}/load-from-files`;
    return this.post<Array<{ name: string; version: number; status: string }>>(
      url,
      {},
    );
  }
  static async loadFromGenerated(): Promise<
    Array<{ name: string; version: number; status: string }>
  > {
    const url = `${this.baseUrl}/${this.plural}/load-from-generated`;
    return this.post<Array<{ name: string; version: number; status: string }>>(
      url,
      {},
    );
  }
  static async reanalyzeAll(): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/reanalyze-all`;
    return this.post<any>(url, {});
  }
  static async reanalyzeOutdated(): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/reanalyze-outdated`;
    return this.post<any>(url, {});
  }
  async reanalyze(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessDesign.plural}/${(this as any).id}/reanalyze`;
    return BusinessObjectBase.post<any>(url, {});
  }
  async suspend(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ProcessDesign.plural}/${(this as any).id}/suspend`;
    return BusinessObjectBase.post<any>(url, {});
  }
  static async upload(options: any): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/upload`;
    return this.post<any>(url, options);
  }
}
